import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Droplets, Hand, Heart, Home, Sun, Thermometer, Utensils } from "lucide-react";
import { Reveal, SectionTag } from "@/components/Reveal";

const CHAPTERS = [
  {
    n: "01",
    icon: Home,
    title: "Terrariet",
    body: "Kronegekkoer lever lodret. Et voksent dyr trives i 45×45×60 cm eller større — unge kan starte mindre. Byg med grene, kork og planter, så der er både klatremuligheder og gemmesteder. Vores håndbyggede beton-baggrunde giver gange og afsatser, som gekkoen bruger hele natten.",
  },
  {
    n: "02",
    icon: Thermometer,
    title: "Temperatur og fugtighed",
    body: "Hold 22–26 °C om dagen og 18–22 °C om natten — almindelig stuetemperatur er ofte nok. Fugtigheden skal ligge på 60–80 %. Dug terrariet med vand hver aften, og lad det tørre let ud i løbet af dagen. Et hygrometer og et termometer er det vigtigste udstyr, du kan købe.",
  },
  {
    n: "03",
    icon: Sun,
    title: "Belysning",
    body: "Kronegekkoer er nat- og skumringsaktive, men de har stadig brug for en fast dagsrytme: giv 12 timer lys og 12 timer mørke hver dag — brug en timer, så cyklussen aldrig skifter. Selv om de gemmer sig om dagen, nyder de godt af svagt UVB-lys (2–5 % skygge-zone-pære), som styrker deres knogler og kalciumoptag. Placér lampen, så der altid er skyggefulde gemmesteder, gekkoen kan trække sig tilbage i. Brug aldrig stærke varmelamper — de udtørrer og overopheder terrariet. Vækstlys (LED) til planterne er fint, så længe det ikke giver varme.",
  },
  {
    n: "04",
    icon: Utensils,
    title: "Foder og vand",
    body: "Hovedføden er foderpulver (fx Gecko Nutrition) blandet med vand — server 2–3 gange om ugen i en skål på en afsats. Supplér gerne med insekter en gang om ugen, drysset med calcium. Fersk vand skal altid være tilgængeligt, også selv om gekkoen helst drikker dugdråber.",
  },
  {
    n: "05",
    icon: Hand,
    title: "Håndtering",
    body: "Kronegekkoer er rolige og kan vænnes til hånden. Start med korte, rolige sessioner efter et par ugers tilvænning — lad altid gekkoen selv gå fra hånd til hånd. Tag aldrig fat i halen: de taber den, og den gror ikke ud igen. Det er helt normalt og gør ikke ondt.",
  },
  {
    n: "06",
    icon: Droplets,
    title: "Fældning og sundhed",
    body: "Gekkoen fælder huden hver 3–6. uge og spiser den selv. Sørg for et ekstra fugtigt skjul i fældningsperioder. Et sundt dyr har klare øjne, en fyldig hale og god appetit. Skriv til os, hvis du er i tvivl — vi kender vores dyr og hjælper gerne.",
  },
  {
    n: "07",
    icon: Heart,
    title: "Sådan kommer din gekko hjem",
    body: "De første 48–72 timer skal din nye gekko have fred til at sætte sig i sit nye hjem — ingen håndtering den første uge. Sæt foder i fra dag ét, men vær ikke bekymret, hvis den ikke spiser med det samme. Alle vores dyr er håndtamme, spiseklare og vant til foderpulver, før de flytter hjemmefra.",
  },
];

export default function PlejeguidePage() {
  const navigate = useNavigate();
  return (
    <div data-testid="plejeguide-page" className="px-6 pb-24 pt-36 lg:px-16 lg:pb-36">
      <Reveal>
        <SectionTag index="09" label="Plejeguide" />
        <h1 className="mt-6 max-w-3xl font-serif text-4xl tracking-tight text-cream sm:text-5xl lg:text-6xl">
          Sådan trives din{" "}
          <span className="italic text-pale">Correlophus ciliatus</span>
        </h1>
        <p className="mt-8 max-w-2xl text-base leading-relaxed text-sage sm:text-lg">
          Kronegekkoen er en af de nemmeste krybdyr at holde — når det grundlæggende er
          på plads. Her er alt, hvad du skal vide, før din gekko flytter ind. Skrevet af
          os, ud fra vores eget opdræt.
        </p>
      </Reveal>

      <div className="mt-20">
        {CHAPTERS.map((c, i) => (
          <Reveal key={c.n} delay={i * 0.05}>
            <div className="grid grid-cols-1 gap-6 border-t border-white/10 py-12 lg:grid-cols-12">
              <span className="flex items-center gap-4 font-mono text-xs tracking-[0.25em] text-forest lg:col-span-2">
                {c.n}
                <c.icon className="h-4 w-4 text-forest/70" strokeWidth={1.5} />
              </span>
              <h2 className="font-serif text-3xl italic tracking-tight text-cream lg:col-span-4">
                {c.title}
              </h2>
              <p className="max-w-xl text-base leading-relaxed text-sage lg:col-span-6">
                {c.body}
              </p>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal>
        <div className="mt-10 flex flex-wrap items-center justify-between gap-6 border border-forest/40 bg-forest/10 p-8">
          <p className="max-w-xl font-serif text-2xl italic leading-snug text-cream">
            Klar til at møde din nye roomie?
          </p>
          <button
            data-testid="plejeguide-morphs-button"
            onClick={() => {
              navigate("/");
              setTimeout(() => window.__lenis?.scrollTo("#morphs", { offset: -64 }), 450);
            }}
            className="group flex items-center gap-3 rounded-full bg-forest px-7 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-pale hover:text-ink"
          >
            Se alle Correlophus ciliatus
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </button>
        </div>
      </Reveal>
    </div>
  );
}
